import {Resolution} from "../../../common/resolution";
import {IMAGE_BREAKPOINTS, ImageBreakpoint} from "../../../common/resolutionConstants";
import {Cookieflag} from "../../../common/cookieflag";
import {isDefined} from "../../../common/utils/basics";
import {ImageDimension, parseResolutionSource, ResolutionSource} from "./imageUtils";
import {resolve} from "../../../container";
import {Timeout} from "../../../common/timeout";
import {ifInitiallyNotIntersected, IntersectionObserverFactory, onceIntersected, ResizeObserverFactory} from "../../../common/observation";
import {customElement, property, query, state} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {RetinaSourceCreator} from "./retinaSourceCreator";
import {eopCustomEvent} from "../../../common/utils/events";
import {UnLitElement} from "../../../common/elements";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {COLOR_SCHEME_CHANGED_EVENT, ColorScheme, type ColorSchemeData, ColorSchemeService} from "../../../common/colorSchemeService";
import {EventBus, EventBusEvent} from "../../../common/eventBus";

export const RESIZE_TIMEOUT_DELAY = 300;
const FIXED_ASPECT_RATIO_CLASS = "fixed-aspect-ratio";

type SrcData = {
    src?: string;
    srcset?: string;
};

@customElement("eop-responsive-image")
export class ResponsiveImageElement extends UnLitElement {

    private resizeObserver: ResizeObserver;
    private intersectionObserver: IntersectionObserver;
    private noIntersectionObserver: IntersectionObserver;
    private resizeTimeout: Maybe<Promise<Promise<void>>>;
    private imageDimension: ImageDimension;
    private breakpointSources: Maybe<Map<ImageBreakpoint, string>>;
    private useFixedAspectRatio: boolean;
    private resolutionSource: ResolutionSource;

    @property({attribute: "image-src", reflect: true})
    private imageSrc: string;
    @property({attribute: "image-alt"})
    private imageAlt: string;
    @property({attribute: "image-src-dark", reflect: true})
    private imageSrcDark: string;
    @property({attribute: "fixed-aspect-ratio", type: Boolean})
    private fixedAspectRatio: boolean = false;
    @property({attribute: "intrinsic-aspect-ratio", type: Boolean, reflect: true})
    private intrinsicAspectRatio: boolean = false;
    @property({attribute: "reverse-src-order", type: Boolean})
    private reverseSrcOrder: boolean = false;
    @property({attribute: "max-crop-threshold", type: Number})
    private maxCropThreshold: Maybe<number>;
    @property({attribute: "functional-icons"})
    private functionalIcons: string = "";
    @property({attribute: "baseplate-in-darkmode", type: Boolean})
    public baseplateInDarkMode: boolean = false;

    @state()
    private src: string;
    @state()
    private srcset: string;
    @state()
    private inactive: boolean;
    @state()
    private enableBaseplate: boolean = false;

    @query("img")
    private imageElement: HTMLImageElement;

    public constructor(
        private cookieflag: Cookieflag = resolve(Cookieflag),
        private timeout: Timeout = resolve(Timeout),
        private resolution: Resolution = resolve(Resolution),
        private retinaSourceCreator: RetinaSourceCreator = resolve(RetinaSourceCreator),
        private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private intersectionObserverFactory: IntersectionObserverFactory = resolve(IntersectionObserverFactory),
        private colorSchemeService: ColorSchemeService = resolve(ColorSchemeService),
        private eventBus: EventBus = resolve(EventBus)
    ) {
        super();
        this.updateImageDimension();
        this.src = "";
        this.resolutionSource = new ResolutionSource();
        this.inactive = true;

        this.classList.add("eop-image");
    }

    public render(): TemplateResult {
        if (this.cookieflag.isSetFor("enbw-disable-responsive-image") || this.inactive) {
            return html`
                <div class="responsive-image">
                    <div class="image-container">
                        <img @load=${this.loaded} src="" alt="" ?baseplate=${this.enableBaseplate}>
                        ${this.renderFunctionalIcons()}
                    </div>
                </div>
            `;
        } else {
            const srcData = this.currentSrcData();
            return html`
                <div class="responsive-image">
                    <div class="image-container">
                        <img @load=${this.loaded} src=${srcData.src} srcset=${srcData.srcset} alt=${this.imageAlt} ?baseplate=${this.enableBaseplate}>
                        ${this.renderFunctionalIcons()}
                    </div>
                </div>
            `;
        }
    }

    private renderFunctionalIcons(): Nullable<TemplateResult> {
        if (this.functionalIcons.length === 0) {
            return null;
        }
        const icons = this.functionalIcons
            .split(",")
            .map((icon: string) => html`
                <div class=${icon}></div>`);

        return html`
            <div class="functional-icons">
                ${icons}
            </div>`;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.useFixedAspectRatio = this.fixedAspectRatio;
        this.resizeObserver = this.resizeObserverFactory.create(() => this.scheduleImageUpdate());

        if (this.hasDarkModeImage()) {
            this.eventBus.on(COLOR_SCHEME_CHANGED_EVENT, (_event: EventBusEvent<ColorSchemeData>) => this.updateSource());
        }

        if (this.baseplateInDarkMode) {
            this.eventBus.on(COLOR_SCHEME_CHANGED_EVENT, (_event: EventBusEvent<ColorSchemeData>) => this.toggleBaseplate());
            this.toggleBaseplate();
        }

        const intersectionOptions = {threshold: 0, rootMargin: "150% 0% 150% 0%"};

        this.intersectionObserver = this.intersectionObserverFactory.create(onceIntersected(() => {
            if (this.willTriggerResizeOnImageLoad()) {
                void this.onInitialLoad(() => {
                    this.updateImageDimension();
                });
            }

            this.resizeObserver.observe(this);
            this.inactive = false;
            this.updateSource().then(() => {
                void this.onInitialLoad(() => this.dispatchEvent(eopCustomEvent("imageLoaded")));
            });
        }), intersectionOptions, "imageLoaded");
        this.intersectionObserver.observe(this);

        this.noIntersectionObserver = this.intersectionObserverFactory.create(ifInitiallyNotIntersected(() => {
            this.dispatchEvent(eopCustomEvent("imageOutsideViewPort"));
        }), intersectionOptions, "imageOutsideViewPort");
        this.noIntersectionObserver.observe(this);
    }

    public disconnectedCallback(): void {
        this.resizeObserver.disconnect();
        this.intersectionObserver.disconnect();
        super.disconnectedCallback();
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        this.breakpointSources = this.extractSources();
    }

    private loaded(): void {
        this.dispatchEvent(eopCustomEvent("imageLoaded"));
    }

    private currentSrcData(): SrcData {
        this.resolutionSource = this.extractCurrentImageSource();
        this.imageDimension = this.determineImageDimension();
        this.imageDimension.limitTo(this.resolutionSource.width, this.resolutionSource.height);
        if (!this.imageDimension.isValid()) {
            return {};
        }

        this.src = this.retinaSourceCreator.resolutionUrlBase(this.resolutionSource, this.imageDimension);
        this.srcset = this.retinaSourceCreator.srcsetFor(this.resolutionSource, this.imageDimension);

        return {
            src: this.src,
            srcset: this.srcset
        };
    }

    private extractSources(): Map<ImageBreakpoint, string> {
        const artDirectedSources = new Map<ImageBreakpoint, string>();
        for (const key of Object.keys(IMAGE_BREAKPOINTS)) {
            const src = this.getAttribute("src-" + IMAGE_BREAKPOINTS[key].min);
            if (src) {
                artDirectedSources.set(IMAGE_BREAKPOINTS[key], src);
            }
        }
        return artDirectedSources;
    }

    private willTriggerResizeOnImageLoad(): boolean {
        return this.clientWidth !== 0
            && this.clientHeight === 0;
    }

    public scheduleImageUpdate(): void {
        this.useFixedAspectRatio = this.fixedAspectRatio && !this.shouldBeCropped();

        if (this.dimensionUnchanged()) {
            return;
        }
        if (this.resizeTimeout) {
            this.timeout.cancel(this.resizeTimeout);
        }
        this.resizeTimeout = this.timeout.delay(() => this.updateSource(), RESIZE_TIMEOUT_DELAY);
    }

    private async onInitialLoad(callback: () => void): Promise<void> {
        await this.updateComplete;
        this.imageElement.addEventListener("load", () => callback(), {once: true});
    }

    private updateImageDimension(): void {
        this.imageDimension = ImageDimension.from(this);
    }

    private async updateSource(): Promise<void> {
        this.resolutionSource = this.extractCurrentImageSource();
        this.addSource();

        await this.updateComplete;
    }

    private extractCurrentImageSource(): ResolutionSource {
        const currentBreakpoint = this.resolution.getBreakpoint();
        let breakpointSource: Nullable<string>;
        if (this.reverseSrcOrder) {
            breakpointSource = Object.keys(IMAGE_BREAKPOINTS)
                .map(key => IMAGE_BREAKPOINTS[key])
                .filter(breakpoint => currentBreakpoint < breakpoint.min)
                .map(breakpoint => this.getSourceForResolution(breakpoint))
                .filter(isDefined)
                .filter(src => !src.startsWith("{{"))
                .getFirst();
        } else {
            breakpointSource = Object.keys(IMAGE_BREAKPOINTS)
                .map(key => IMAGE_BREAKPOINTS[key])
                .filter(breakpoint => breakpoint.min <= currentBreakpoint)
                .map(breakpoint => this.getSourceForResolution(breakpoint))
                .filter(isDefined)
                .filter(src => !src.startsWith("{{"))
                .getLast();
        }
        return parseResolutionSource(breakpointSource ?? this.currentImageSrc()) ?? new ResolutionSource();
    }

    private addSource(): void {
        this.resolutionSource = this.extractCurrentImageSource();
        this.imageDimension = this.determineImageDimension();
        if (!this.imageDimension.isValid()) {
            return;
        }
        const imageLargeEnough = this.imageDimension.isApplicableTo(this.resolutionSource);
        this.classList.toggle(FIXED_ASPECT_RATIO_CLASS, this.useFixedAspectRatio && imageLargeEnough);

        this.imageDimension.limitTo(this.resolutionSource.width, this.resolutionSource.height);

        this.srcset = this.retinaSourceCreator.srcsetFor(this.resolutionSource, this.imageDimension);
        this.src = this.retinaSourceCreator.resolutionUrlBase(this.resolutionSource, this.imageDimension);
    }

    private getSourceForResolution(imageBreakpoint: ImageBreakpoint): Maybe<string> {
        this.breakpointSources ??= this.extractSources();
        return this.breakpointSources.get(imageBreakpoint);
    }

    private determineImageDimension(): ImageDimension {
        const imageDimension = ImageDimension.from(this);
        if (this.useFixedAspectRatio || this.intrinsicAspectRatio) {
            imageDimension.applyAspectRatio(this.resolutionSource.width / this.resolutionSource.height);
        }
        return imageDimension;
    }

    private dimensionUnchanged(): boolean {
        const currentDimension = ImageDimension.from(this);
        return this.imageDimension.matches(currentDimension);
    }

    private shouldBeCropped(): boolean {
        if (!this.maxCropThreshold) {
            return false;
        }

        if (!this.resolutionSource.validate()) {
            return false;
        }

        let relativeImageArea;

        const imgRatio = this.resolutionSource.width / this.resolutionSource.height;
        const containerRatio = this.clientWidth / this.clientHeight;

        if (containerRatio > imgRatio) {
            relativeImageArea = this.clientWidth * this.resolutionSource.height * (this.clientWidth / this.resolutionSource.width);
        } else {
            relativeImageArea = this.clientHeight * this.resolutionSource.width * (this.clientHeight / this.resolutionSource.height);
        }

        const containerArea = this.clientWidth * this.clientHeight;
        const overlapRatio = containerArea / relativeImageArea;

        return overlapRatio > 1 - this.maxCropThreshold;
    }

    private toggleBaseplate(): void {
        this.enableBaseplate = this.baseplateInDarkMode && this.colorSchemeService.getColorScheme() === ColorScheme.DARK;
    }

    private hasDarkModeImage(): boolean {
        return !!this.imageSrcDark;
    }

    private currentImageSrc(): string {
        if (this.hasDarkModeImage() && this.colorSchemeService.getColorScheme() === ColorScheme.DARK) {
            return this.imageSrcDark;
        }
        return this.imageSrc;
    }
}